import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Star, MapPin, Phone, Mail, Building2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SponsorsPublicosPage = () => {
  const navigate = useNavigate();
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSponsors();
  }, []);

  const fetchSponsors = async () => {
    try {
      const response = await axios.get(`${API}/sponsors/publico`);
      setSponsors(response.data);
    } catch (error) {
      console.error("Error fetching sponsors:", error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to get proper image URL
  const getImageUrl = (url) => {
    if (!url) return null;
    // If it's already a full URL, use it directly
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Otherwise prepend backend URL
    return `${BACKEND_URL}${url}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-warm-bone">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-zuno-wine"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-bone py-12">
      <div className="container mx-auto px-6 md:px-12 lg:px-24">
        <Button
          onClick={() => navigate("/")}
          variant="ghost"
          className="mb-8"
          data-testid="back-button"
        >
          <ArrowLeft className="mr-2" size={20} />
          Volver al Inicio
        </Button>

        <div className="glass-card rounded-3xl p-8 md:p-12">
          <div className="flex items-center gap-4 mb-8">
            <Star className="h-12 w-12 text-zuno-wine" fill="#722F37" />
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-zuno-wine">
                Nuestros Patrocinadores
              </h1>
              <p className="text-lg text-slate-600 mt-2">
                Gracias por hacer posible este evento
              </p>
            </div>
          </div>

          {sponsors.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-xl text-slate-600 mb-6">
                Aún no hay patrocinadores registrados
              </p>
              <Button
                onClick={() => navigate("/sponsors")}
                className="btn-primary"
                data-testid="become-sponsor-btn"
              >
                <Star className="mr-2" fill="white" />
                ¡Sé el Primero en Patrocinar!
              </Button>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {sponsors.map((sponsor, index) => (
                  <Card
                    key={sponsor.id}
                    className="p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
                    data-testid={`sponsor-${index}`}
                  >
                    {/* Header with package badge */}
                    <div className="flex justify-between items-start mb-4">
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          sponsor.paquete === "Limón"
                            ? "bg-yellow-100 text-yellow-800"
                            : sponsor.paquete === "Guille"
                            ? "bg-purple-100 text-purple-800"
                            : "bg-blue-100 text-blue-800"
                        }`}
                      >
                        {sponsor.paquete}
                      </span>
                    </div>

                    {/* Business Name */}
                    <h3 className="text-2xl font-bold text-slate-800 mb-4">
                      {sponsor.nombre_negocio}
                    </h3>

                    {/* Business Info */}
                    <div className="space-y-2 text-sm text-slate-600 mb-4">
                      {sponsor.giro && (
                        <p className="flex items-start gap-2">
                          <Building2 size={16} className="mt-0.5 flex-shrink-0 text-zuno-wine" />
                          <span><strong>Giro:</strong> {sponsor.giro}</span>
                        </p>
                      )}
                      {sponsor.direccion && (
                        <p className="flex items-start gap-2">
                          <MapPin size={16} className="mt-0.5 flex-shrink-0 text-zuno-wine" />
                          <span><strong>Dirección:</strong> {sponsor.direccion}</span>
                        </p>
                      )}
                      {sponsor.contacto_nombre && (
                        <p className="flex items-start gap-2">
                          <Star size={16} className="mt-0.5 flex-shrink-0 text-zuno-wine" />
                          <span><strong>Contacto:</strong> {sponsor.contacto_nombre}</span>
                        </p>
                      )}
                      {sponsor.contacto_email && (
                        <p className="flex items-start gap-2">
                          <Mail size={16} className="mt-0.5 flex-shrink-0 text-zuno-wine" />
                          <span><strong>Email:</strong> {sponsor.contacto_email}</span>
                        </p>
                      )}
                      {sponsor.contacto_telefono && (
                        <p className="flex items-start gap-2">
                          <Phone size={16} className="mt-0.5 flex-shrink-0 text-zuno-wine" />
                          <span><strong>Teléfono:</strong> {sponsor.contacto_telefono}</span>
                        </p>
                      )}
                    </div>

                    {/* Logo */}
                    {sponsor.logo_url && (
                      <div className="mt-4 pt-4 border-t">
                        <img
                          src={getImageUrl(sponsor.logo_url)}
                          alt={sponsor.nombre_negocio}
                          className="max-h-32 max-w-full object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </Card>
                ))}
              </div>

              <div className="text-center">
                <p className="text-lg text-slate-600 mb-4">
                  ¿Quieres ser parte de nuestros patrocinadores?
                </p>
                <Button
                  onClick={() => navigate("/sponsors")}
                  className="btn-primary"
                  data-testid="join-sponsors-btn"
                >
                  <Star className="mr-2" fill="white" />
                  Ser Patrocinador
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SponsorsPublicosPage;
